import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

/* ======================= Helpers ======================= */
const pm = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const cx = (k) => (k === 1 ? "x" : k === -1 ? "-x" : `${k}x`);
const par = (n) => (n < 0 ? `(${n})` : `${n}`);
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

/* ===== generator równań ax+b = cx+d o zadanym typie ===== */
function genLinearEquation(targetType = "oznaczone") {
  const pickNZ = () => {
    let v;
    do v = rand(-9, 9); while (v === 0);
    return v;
  };

  let a, b, c, d, correctType;

  if (targetType === "oznaczone") {
    a = pickNZ();
    c = pickNZ();
    while (a === c) c = pickNZ();
    b = rand(-10, 10);
    d = rand(-10, 10);
    correctType = "oznaczone";
  } else if (targetType === "tozsamosciowe") {
    // nie używamy generatora do trywialnych ax+b=ax+b — to idzie z puli nietrywialnych
    a = 1; b = 0; c = 1; d = 0; // placeholder (niezwracany)
    correctType = "tozsamosciowe";
  } else { // sprzeczne
    a = pickNZ();
    c = a;
    b = rand(-10, 10);
    d = b;
    while (d === b) d = rand(-10, 10);
    correctType = "sprzeczne";
  }

  const equationLatex = `${cx(a)} ${pm(b)} = ${cx(c)} ${pm(d)}`;
  return { a, b, c, d, equationLatex, correctType };
}

/* Nietrywialne tożsamości (redukują się do 0=0, ale nie są identyczne na starcie) */
const nonTrivialIdentityExamples = [
  { equation: "x - (x - 5) = 5", correctType: "tozsamosciowe" },
  { equation: "\\frac{3(2x - 3)}{2} = 3x - 4.5", correctType: "tozsamosciowe" },
  { equation: "(x + 1)(x - 1) + 1 = x^2", correctType: "tozsamosciowe" },
];

/* ======================= Generate ======================= */
export function generateTask() {
  // 70% generator (oznaczone/sprzeczne), 30% gotowce (w tym nietrywialne tożsamości)
  const useGenerator = Math.random() < 0.7;

  if (useGenerator) {
    const kinds = ["oznaczone", "tozsamosciowe", "sprzeczne"];
    const target = kinds[rand(0, kinds.length - 1)];

    // Tożsamości losujemy z nietrywialnych przykładów (bez ax+b=ax+b).
    if (target === "tozsamosciowe") {
      const picked =
        nonTrivialIdentityExamples[
          rand(0, nonTrivialIdentityExamples.length - 1)
        ];
      return {
        type: "EquationTypeIdentificationTask",
        data: {
          equation: picked.equation,
          correctType: picked.correctType,
          generated: false, // aby użyć ręcznych wyjaśnień
        },
      };
    }

    // dla oznaczonych/sprzecznych — generator
    const g = genLinearEquation(target);
    return {
      type: "EquationTypeIdentificationTask",
      data: {
        equation: g.equationLatex,
        correctType: g.correctType,
        a: g.a, b: g.b, c: g.c, d: g.d,
        generated: true,
      },
    };
  }

  // --- stare przykłady + dorzucone nietrywialne tożsamości ---
  const examples = [
    { equation: "x + (x - 3) = 5", correctType: "oznaczone" },
    { equation: "x - (x - 4) = 5", correctType: "sprzeczne" },
    { equation: "x - (x - 5) = 5", correctType: "tozsamosciowe" },
    { equation: "\\frac{3(5x - 1)}{5} = 3x", correctType: "sprzeczne" },
    { equation: "\\frac{2(x + 5)}{3} = 0", correctType: "oznaczone" },
    { equation: "\\frac{3(2x - 3)}{2} = 3x - 4.5", correctType: "tozsamosciowe" },
    { equation: "(x - 2)(x + 2) = x^2", correctType: "sprzeczne" },
    { equation: "(x - 3)^2 = x^2", correctType: "oznaczone" },
    { equation: "(x + 1)(x - 1) + 1 = x^2", correctType: "tozsamosciowe" },
  ];

  const fullExamples = [...examples, ...nonTrivialIdentityExamples];
  const picked = fullExamples[Math.floor(Math.random() * fullExamples.length)];
  return {
    type: "EquationTypeIdentificationTask",
    data: {
      equation: picked.equation,
      correctType: picked.correctType,
      generated: false,
    },
  };
}

/* ======================= Render task ======================= */
export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Określ czy równanie jest sprzeczne, tożsamościowe bądź oznaczone:</p>
      <BlockMath math={data.equation} />
    </div>
  );
}

/* ======================= Input (SelectInput jak w SignOfLineTask) ======================= */
export function renderInput(_, userAnswer, onChange, disabled) {
  const options = [
    { value: "sprzeczne",     label: "równanie sprzeczne" },
    { value: "tozsamosciowe", label: "równanie tożsamościowe" },
    { value: "oznaczone",     label: "równanie oznaczone" }, // ← bez dopisku
  ];

  return (
    <div style={{ maxWidth: 360, marginTop: "0.75rem" }}>
      <div style={{ marginBottom: 6 }}>Wybierz typ równania:</div>
      <SelectInput
        value={userAnswer || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        options={options}
      />
    </div>
  );
}


/* ======================= Check (spójne z SignOfLineTask) ======================= */
export function checkAnswer({ data }, userAnswer) {
  const expected = data.correctType;
  const isCorrect = userAnswer === expected;

  return {
    isCorrect,
    expected,          // "sprzeczne" | "tozsamosciowe" | "oznaczone"
    actual: userAnswer || null,
    comment: null,
  };
}

/* ======================= Answer & User Answer (styl jak w SignOfLineTask) ======================= */
const typeLabel = (v) =>
  v === "sprzeczne"     ? "równanie sprzeczne" :
  v === "tozsamosciowe" ? "równanie tożsamościowe" :
  v === "oznaczone"     ? "równanie oznaczone" :
  v;

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <div style={{ display: "inline-flex", padding: "6px 10px",
                    borderRadius: 8, background: "rgba(255,255,255,0.06)" }}>
        {typeLabel(data.correctType)}
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <div style={{ display: "inline-flex", padding: "6px 10px",
                    borderRadius: 8, background: "rgba(255,255,255,0.06)" }}>
        {typeLabel(userAnswer)}
      </div>
    </div>
  );
}

/* ======================= Explanation ======================= */
export function renderExplanation({ data }) {
  const { equation, correctType, generated, a, b, c, d } = data;

  /* ---- ręczne wyjaśnienia dla wybranych gotowców (skrócone, scalone kroki) ---- */
  const manual = {
    // osobne linie dla +3 i :2
    "x + (x - 3) = 5": (
      <>
        <p><strong>Krok 1:</strong> Dodaj składniki z lewej:</p>
        <BlockMath math={"x + (x - 3) = 2x - 3"} />

        <p><strong>Krok 2:</strong> Przenieś 3 na drugą stronę:</p>
        <BlockMath math={"2x - 3 = 5\\;\\Big|\\ +\\ 3"} />
        <BlockMath math={"2x = 8"} />

        <p><strong>Krok 3:</strong> Podziel obie strony przez 2:</p>
        <BlockMath math={"2x = 8\\;\\Big|\\ :\\ 2"} />
        <BlockMath math={"x = 4"} />
      </>
    ),

    "x - (x - 4) = 5": (
      <>
        <p><strong>Krok 1:</strong> Rozwiń nawias i uprość:</p>
        <BlockMath math={"x - x + 4 = 5"} />
        <BlockMath math={"4 = 5"} />
        <p><strong>Krok 2:</strong> Sprzeczność.</p>
      </>
    ),

    // TOŻSAMOŚCIOWE: domknięcie do 0 = 0
    "x - (x - 5) = 5": (
      <>
        <p><strong>Krok 1:</strong> Rozwiń i uprość:</p>
        <BlockMath math={"x - x + 5 = 5"} />
        <BlockMath math={"5 = 5"} />

        <p><strong>Krok 2:</strong> Zredukuj obie strony:</p>
        <BlockMath math={"5 = 5\\;\\Big|\\ -\\ 5"} />
        <BlockMath math={"0 = 0"} />
      </>
    ),

    // SPRZECZNE: usuń mianownik, zredukuj
    "\\frac{3(5x - 1)}{5} = 3x": (
      <>
        <p><strong>Krok 1:</strong> Pomnóż obie strony przez 5 (usuń mianownik):</p>
        <BlockMath math={"\\frac{3(5x - 1)}{5} = 3x\\;\\Big|\\ \\cdot\\ 5"} />
        <BlockMath math={"3(5x - 1) = 15x"} />

        <p><strong>Krok 2:</strong> Rozwiń nawias i zredukuj:</p>
        <BlockMath math={"15x - 3 = 15x\\;\\Big|\\ -\\ 15x"} />
        <BlockMath math={"-3 = 0"} />
      </>
    ),

    // OZNACZONE: po mnożeniu szybka redukcja
    "\\frac{2(x + 5)}{3} = 0": (
      <>
        <p><strong>Krok 1:</strong> Pomnóż obie strony przez 3 (usuń mianownik):</p>
        <BlockMath math={"\\frac{2(x + 5)}{3} = 0\\;\\Big|\\ \\cdot\\ 3"} />
        <BlockMath math={"2(x + 5) = 0"} />

        <p><strong>Krok 2:</strong> Rozwiń i zredukuj:</p>
        <BlockMath math={"2x + 10 = 0\\;\\Big|\\ -\\ 10,\\ :\\ 2"} />
        <BlockMath math={"x = -5"} />
      </>
    ),

    // TOŻSAMOŚCIOWE: *2, potem redukcja do 0=0 w jednym kroku
    "\\frac{3(2x - 3)}{2} = 3x - 4.5": (
      <>
        <p><strong>Krok 1:</strong> Pomnóż obie strony przez 2 (usuń mianownik):</p>
        <BlockMath math={"\\frac{3(2x - 3)}{2} = 3x - 4.5\\;\\Big|\\ \\cdot\\ 2"} />
        <BlockMath math={"3(2x - 3) = 2(3x - 4.5)"} />

        <p><strong>Krok 2:</strong> Rozwiń nawiasy i zredukuj:</p>
        <BlockMath math={"6x - 9 = 6x - 9\\;\\Big|\\ -\\ 6x,\\ +\\ 9"} />
        <BlockMath math={"0 = 0"} />
      </>
    ),

    "(x - 2)(x + 2) = x^2": (
      <>
        <p><strong>Krok 1:</strong> Rozwiń i zredukuj:</p>
        <BlockMath math={"x^2 - 4 = x^2\\;\\Big|\\ -\\ x^2"} />
        <BlockMath math={"-4 = 0"} />
        <p><strong>Krok 2:</strong> Sprzeczność.</p>
      </>
    ),

    // OZNACZONE
    "(x - 3)^2 = x^2": (
      <>
        <p><strong>Krok 1:</strong> Rozwiń i odejmij \\(x^2\\):</p>
        <BlockMath math={"x^2 - 6x + 9 = x^2\\;\\Big|\\ -\\ x^2"} />
        <BlockMath math={"-6x + 9 = 0"} />

        <p><strong>Krok 2:</strong> Zredukuj:</p>
        <BlockMath math={"-6x + 9 = 0\\;\\Big|\\ -\\ 9,\\ :\\ (-6)"} />
        <BlockMath math={"x = 1.5"} />
      </>
    ),

    // TOŻSAMOŚCIOWE
    "(x + 1)(x - 1) + 1 = x^2": (
      <>
        <p><strong>Krok 1:</strong> Rozwiń i uprość:</p>
        <BlockMath math={"(x^2 - 1) + 1 = x^2"} />
        <BlockMath math={"x^2 = x^2"} />

        <p><strong>Krok 2:</strong> Zredukuj obie strony:</p>
        <BlockMath math={"x^2 = x^2\\;\\Big|\\ -\\ x^2"} />
        <BlockMath math={"0 = 0"} />
      </>
    ),
  };

  const reasons = {
    oznaczone:
      "Po przekształceniach otrzymujemy równanie w postaci (a−c)x = d−b z (a−c) ≠ 0, więc istnieje dokładnie jedno rozwiązanie — równanie oznaczone.",
    tozsamosciowe:
      "Po przekształceniach obie strony są identyczne (np. 0 = 0 lub x = x). Zatem równanie jest prawdziwe dla każdego x — równanie tożsamościowe.",
    sprzeczne:
      "Po przekształceniach dostajemy fałsz (np. 0 = liczba ≠ 0). Zatem równanie nie ma rozwiązań — równanie sprzeczne.",
  };

  // jeśli to nie było generowane i mamy ręczne wyjaśnienie — użyj go
  if (!generated && manual[equation]) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
        {manual[equation]}
        <p><strong>Uzasadnienie:</strong> {reasons[correctType]}</p>
      </div>
    );
  }

  /* ---- ogólny builder dla równań generowanych: ax+b = cx+d ---- */
  const start = `${cx(a)} ${pm(b)} = ${cx(c)} ${pm(d)}`;

  // Krok 1: przenieś wyrazy z x na lewo
  const verbCx = c >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const opCx   = c >= 0 ? `- ${c}x` : `+ ${Math.abs(c)}x`;
  const AminusC = a - c;

  // Krok 2: przenieś stałe na prawo
  const verbB = b >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const opB   = b >= 0 ? `- ${b}` : `+ ${Math.abs(b)}`;
  const DminusB = d - b;

  if (a === c) {
    // po Kroku 1: b = d → szybko do 0 = 0 lub 0 = liczba
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
        <p><strong>Krok 1:</strong> {verbCx} {Math.abs(c)}<InlineMath math="x" />.</p>
        <BlockMath math={`${start}\\;\\Big|\\ ${opCx}`} />
        <BlockMath math={`${b} = ${d}`} />

        <p><strong>Krok 2:</strong> Zredukuj obie strony:</p>
        <BlockMath math={`${b} = ${d}\\;\\Big|\\ ${b >= 0 ? "-\\ " + b : "+\\ " + Math.abs(b)}`} />
        <BlockMath math={`${0} = ${d - b}`} />

        {correctType === "tozsamosciowe" && (
          <>
            <p><strong>Krok 3:</strong> Obie strony identyczne:</p>
            <BlockMath math={`0 = 0`} />
          </>
        )}
        {correctType === "sprzeczne" && (
          <>
            <p><strong>Krok 3:</strong> Sprzeczność:</p>
            <BlockMath math={`0 = ${d - b}`} />
          </>
        )}

        <p><strong>Uzasadnienie:</strong> {reasons[correctType]}</p>
      </div>
    );
  }

  // przypadek oznaczone (a ≠ c)
  const denPar = (AminusC < 0 ? `(${AminusC})` : `${AminusC}`);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
      {/* Krok 1 */}
      <p><strong>Krok 1:</strong> {verbCx} {Math.abs(c)}<InlineMath math="x" />.</p>
      <BlockMath math={`${start}\\;\\Big|\\ ${opCx}`} />
      <BlockMath math={`${cx(a - c)} ${pm(b)} = ${d}`} />

      {/* Krok 2 */}
      <p><strong>Krok 2:</strong> {verbB} {Math.abs(b)}.</p>
      <BlockMath math={`${cx(a - c)} ${pm(b)} = ${d}\\;\\Big|\\ ${opB}`} />
      <BlockMath math={`${cx(a - c)} = ${DminusB}`} />

      {/* Krok 3 */}
      <p><strong>Krok 3:</strong> Podziel obie strony przez <InlineMath math={denPar} />.</p>
      <BlockMath math={`${cx(a - c)} = ${DminusB}\\;\\Big|\\ :\\ ${denPar}`} />
      <BlockMath math={`x = \\tfrac{${DminusB}}{${AminusC < 0 ? `(${AminusC})` : `${AminusC}`}}`} />

      <p><strong>Uzasadnienie:</strong> {reasons[correctType]}</p>
    </div>
  );
}
